// src/components/admin/Consumptions.js
import React, {
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
} from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import PermissionDenied from '../common/PermissionDenied';
import ToastNotification from '../common/ToastNotification';
import { assetService } from '../../services/assetService';

import { consumableSpareLogService } from '../../services/consumableSpareLogService';

import {
  Box,
  Container,
  Typography,
  Paper,
  Button,
  Grid,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  Alert,
  CircularProgress,
  Skeleton,
  InputAdornment,
  IconButton,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  Card,
  CardContent,
  CardActionArea,
  Tooltip,
} from '@mui/material';

import InventoryIcon            from '@mui/icons-material/Inventory';
import BuildIcon                from '@mui/icons-material/Build';
import SearchIcon               from '@mui/icons-material/Search';
import ClearIcon                from '@mui/icons-material/Clear';
import CheckCircleIcon          from '@mui/icons-material/CheckCircle';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import SaveIcon                 from '@mui/icons-material/Save';
import HistoryIcon              from '@mui/icons-material/History';
import WarningAmberIcon         from '@mui/icons-material/WarningAmber';
import ArrowBackIcon            from '@mui/icons-material/ArrowBack';

// ─────────────────────────────────────────────────────────────────────────────
// CONSTANTS
// ─────────────────────────────────────────────────────────────────────────────

const ITEM_TYPES = [
  {
    value:         'Consumable',
    label:         'Consumable',
    description:   'Items consumed during operation (e.g. Toner, Ink, Ribbon)',
    IconComponent: InventoryIcon,
    color:         'warning',
  },
  {
    value:         'Spare Part',
    label:         'Spare Part',
    description:   'Replacement parts kept for future maintenance use',
    IconComponent: BuildIcon,
    color:         'info',
  },
];

// FIX #9: Only include assets with an explicit matching assetType
const TARGET_ASSET_TYPES = ['Primary Asset', 'Sub Asset'];

// ─────────────────────────────────────────────────────────────────────────────
// SKELETON
// ─────────────────────────────────────────────────────────────────────────────

const ConsumptionsSkeleton = () => (
  <Container
    maxWidth="xl"
    sx={{ mt: 4, mb: 4, display: 'flex', flexDirection: 'column', gap: 3 }}
  >
    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
      <Box>
        <Skeleton variant="text" width={280} height={44} sx={{ borderRadius: 1 }} />
        <Skeleton variant="text" width={360} height={20} sx={{ borderRadius: 1, mt: 0.5 }} />
      </Box>
      <Skeleton variant="rounded" width={100} height={36} sx={{ borderRadius: 2 }} />
    </Box>
    <Paper variant="outlined" sx={{ borderRadius: 3, p: 3 }}>
      <Skeleton variant="rounded" height={200} sx={{ borderRadius: 2 }} />
    </Paper>
    <Paper variant="outlined" sx={{ borderRadius: 3, p: 3 }}>
      <Skeleton variant="rounded" height={300} sx={{ borderRadius: 2 }} />
    </Paper>
  </Container>
);

// ─────────────────────────────────────────────────────────────────────────────
// HISTORY ROW SKELETON
// ─────────────────────────────────────────────────────────────────────────────

const HistoryRowSkeleton = ({ rows = 3 }) => (
  <>
    {Array.from({ length: rows }).map((_, i) => (
      <TableRow key={i}>
        {Array.from({ length: 8 }).map((__, j) => (
          <TableCell key={j}>
            <Skeleton variant="text" height={20} sx={{ borderRadius: 1 }} />
          </TableCell>
        ))}
      </TableRow>
    ))}
  </>
);

// ─────────────────────────────────────────────────────────────────────────────
// HELPERS
// ─────────────────────────────────────────────────────────────────────────────

const formatDate = (dateStr) => {
  if (!dateStr) return '—';
  try {
    return new Date(dateStr).toLocaleDateString('en-GB');
  } catch {
    return '—';
  }
};

const todayISO = () => new Date().toISOString().split('T')[0];

// ─────────────────────────────────────────────────────────────────────────────
// MAIN COMPONENT
// ─────────────────────────────────────────────────────────────────────────────

const Consumptions = () => {
  const { currentUser, loading: authLoading } = useAuth();
  const navigate = useNavigate();

  // ── Auth / page state ─────────────────────────────────────────────────────
  const [pageState,       setPageState]       = useState('loading');
  const [permissionError, setPermissionError] = useState(null);

  // ── Notification ──────────────────────────────────────────────────────────
  const [notification, setNotification] = useState(null);
  const notifTimerRef = useRef(null);

  // FIX #15: showNotification is stable (useCallback with [] deps) — no ref needed
  const showNotification = useCallback((message, type = 'success') => {
    if (notifTimerRef.current) clearTimeout(notifTimerRef.current);
    setNotification({ message, type });
    notifTimerRef.current = setTimeout(() => setNotification(null), 5000);
  }, []);

  useEffect(() => {
    return () => {
      if (notifTimerRef.current) clearTimeout(notifTimerRef.current);
    };
  }, []);

  // ── Data ──────────────────────────────────────────────────────────────────
  const [allAssets,          setAllAssets]          = useState([]);
  const [consumptionHistory, setConsumptionHistory] = useState([]);
  const [assetsLoading,      setAssetsLoading]      = useState(true);
  const [historyLoading,     setHistoryLoading]     = useState(true);

  // ── Stepper state ─────────────────────────────────────────────────────────
  const [activeStep,       setActiveStep]       = useState(0);
  const [selectedItemType, setSelectedItemType] = useState('');
  const [itemSearch,       setItemSearch]       = useState('');
  const [selectedItem,     setSelectedItem]     = useState(null);
  const [targetSearch,     setTargetSearch]     = useState('');
  const [selectedTarget,   setSelectedTarget]   = useState(null);
  const [consumedQty,      setConsumedQty]      = useState('1');
  const [qtyTouched,       setQtyTouched]       = useState(false);
  const [consumptionDate,  setConsumptionDate]  = useState(todayISO());
  const [remarks,          setRemarks]          = useState('');
  const [submitting,       setSubmitting]       = useState(false);

  // History pagination
  const [histPage,        setHistPage]        = useState(0);
  const [histRowsPerPage, setHistRowsPerPage] = useState(10);

  // Sentinel ref for IntersectionObserver
  const historySentinelRef = useRef(null);
  const historyLoadedRef   = useRef(false);

  // Track mounted state to avoid setState after unmount
  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);

  // ─────────────────────────────────────────────────────────────────────────
  // LOAD ASSETS
  // ─────────────────────────────────────────────────────────────────────────

const loadAssets = useCallback(
  async (signal) => {
    if (!currentUser) return;
    if (mountedRef.current) setAssetsLoading(true);
    try {
      const assets = await assetService.fetchAssets({
        companyId: currentUser.companyId || "",
      });
        if (signal?.aborted || !mountedRef.current) return;
        setAllAssets(assets);
        setPageState('ready');
      } catch (err) {
        if (err.name === 'AbortError' || !mountedRef.current) return;
        showNotification(`Failed to load assets: ${err.message}`, 'error');
        setPageState('ready');
      } finally {
        if (!signal?.aborted && mountedRef.current) setAssetsLoading(false);
      }
    },
    [currentUser, showNotification],
  );

  // ─────────────────────────────────────────────────────────────────────────
  // LOAD HISTORY — FIX #1 #12: use consumableSpareLogService
  // ─────────────────────────────────────────────────────────────────────────

  const loadHistory = useCallback(
    async (signal) => {
      if (!currentUser) return;
      if (mountedRef.current) setHistoryLoading(true);
      try {
        // FIX #1 #12: was assetService.getConsumptionHistory (does not exist)
        const history = await consumableSpareLogService.getConsumptionHistory(
          currentUser, {}, 200,
        );
        if (signal?.aborted || !mountedRef.current) return;
        setConsumptionHistory(history);
      } catch (err) {
        if (err.name === 'AbortError' || !mountedRef.current) return;
        showNotification(`Failed to load history: ${err.message}`, 'error');
      } finally {
        if (!signal?.aborted && mountedRef.current) setHistoryLoading(false);
      }
    },
    [currentUser, showNotification],
  );

  // ─────────────────────────────────────────────────────────────────────────
  // BOOTSTRAP
  // ─────────────────────────────────────────────────────────────────────────

  useEffect(() => {
    if (authLoading) return;

    if (!currentUser) {
      setPermissionError('Authentication required. Please log in.');
      setPageState('error');
      return;
    }

    const allowed =
      currentUser.role === 'superadmin' ||
      currentUser.role === 'admin'      ||
      (currentUser.role === 'locationAdmin' && currentUser.companyId);

    if (!allowed) {
      setPermissionError('Access Denied: Administrator privileges required.');
      setPageState('error');
      return;
    }

    const controller = new AbortController();
    loadAssets(controller.signal);
    return () => controller.abort();
  }, [currentUser, authLoading, loadAssets]);

  // ─────────────────────────────────────────────────────────────────────────
  // INTERSECTION OBSERVER — lazy history load
  // ─────────────────────────────────────────────────────────────────────────

  useEffect(() => {
    if (pageState !== 'ready') return;

    const sentinel = historySentinelRef.current;
    if (!sentinel) return;

    const controller = new AbortController();

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && !historyLoadedRef.current) {
          historyLoadedRef.current = true;
          loadHistory(controller.signal);
        }
      },
      { rootMargin: '200px' },
    );

    observer.observe(sentinel);
    return () => {
      observer.disconnect();
      controller.abort();
    };
  }, [pageState, loadHistory]);

  // Reset history page when list length changes
  useEffect(() => {
    setHistPage(0);
  }, [consumptionHistory.length]);

  // ─────────────────────────────────────────────────────────────────────────
  // REFRESH HISTORY (called after successful submission)
  // ─────────────────────────────────────────────────────────────────────────

  const refreshHistory = useCallback(() => {
    const controller = new AbortController();
    loadHistory(controller.signal);
  }, [loadHistory]);

  // ── Derived lists ─────────────────────────────────────────────────────────

  const assetLookup = useMemo(() => {
    const map = {};
    allAssets.forEach((a) => { map[a.id] = a; });
    return map;
  }, [allAssets]);

  const availableItems = useMemo(() => {
    if (!selectedItemType) return [];
    return allAssets.filter((a) => {
      const qty = parseFloat(a.quantity);
      return (
        a.assetType === selectedItemType &&
        !isNaN(qty) && qty > 0        &&
        a.status !== 'Disposed'        &&
        a.status !== 'Returned'
      );
    });
  }, [allAssets, selectedItemType]);

  const filteredItems = useMemo(() => {
    if (!itemSearch.trim()) return availableItems;
    const term = itemSearch.toLowerCase();
    return availableItems.filter(
      (a) =>
        (a.assetName && a.assetName.toLowerCase().includes(term)) ||
        (a.assetId   && a.assetId.toLowerCase().includes(term)),
    );
  }, [availableItems, itemSearch]);

  // FIX #9: Only include assets with an explicit assetType in TARGET_ASSET_TYPES
  const availableTargets = useMemo(
    () =>
      allAssets.filter(
        (a) =>
          TARGET_ASSET_TYPES.includes(a.assetType) &&
          a.status !== 'Disposed'                  &&
          a.status !== 'Returned',
      ),
    [allAssets],
  );

  const filteredTargets = useMemo(() => {
    if (!targetSearch.trim()) return availableTargets;
    const term = targetSearch.toLowerCase();
    return availableTargets.filter(
      (a) =>
        (a.assetName && a.assetName.toLowerCase().includes(term)) ||
        (a.assetId   && a.assetId.toLowerCase().includes(term)),
    );
  }, [availableTargets, targetSearch]);

  const availableQty     = parseFloat(selectedItem?.quantity) || 0;
  const parsedConsumeQty = Number(consumedQty);

  // FIX #6: Unified qty validation — covers empty, NaN, <=0, and exceeds stock
  const qtyNumericError =
    consumedQty === '' ||
    !Number.isFinite(parsedConsumeQty) ||
    parsedConsumeQty <= 0;

  const qtyExceedsStock =
    Number.isFinite(parsedConsumeQty) && parsedConsumeQty > availableQty;

  const qtyError = qtyTouched && (qtyNumericError || qtyExceedsStock);

  const qtyHelperText = qtyError
    ? qtyNumericError
      ? 'Please enter a valid quantity greater than zero'
      : `Exceeds available stock (${availableQty} ${selectedItem?.unitOfMeasurement || 'units'})`
    : `Max available: ${availableQty} ${selectedItem?.unitOfMeasurement || 'units'}`;

  // Whether the form is ready to submit
  const canSubmit =
    Boolean(selectedItem)   &&
    Boolean(selectedTarget) &&
    Boolean(consumptionDate) &&
    !qtyNumericError        &&
    !qtyExceedsStock;

  // ── Step navigation ───────────────────────────────────────────────────────

  const handleSelectItemType = useCallback((type) => {
    setSelectedItemType(type);
    setSelectedItem(null);
    setSelectedTarget(null);
    setItemSearch('');
    setActiveStep(1);
  }, []);

  const handleSelectItem = useCallback((item) => {
    setSelectedItem(item);
    setSelectedTarget(null);
    setActiveStep(2);
  }, []);

  const handleSelectTarget = useCallback((target) => {
    setSelectedTarget(target);
    setActiveStep(3);
  }, []);

  const handleBack = useCallback(() => {
    setActiveStep((prev) => {
      const next = Math.max(0, prev - 1);
      if (prev === 2) setSelectedTarget(null);
      if (prev === 1) setSelectedItem(null);
      return next;
    });
  }, []);

  const handleReset = useCallback(() => {
    setActiveStep(0);
    setSelectedItemType('');
    setSelectedItem(null);
    setSelectedTarget(null);
    setItemSearch('');
    setTargetSearch('');
    setConsumedQty('1');
    setQtyTouched(false);
    setConsumptionDate(todayISO());
    setRemarks('');
  }, []);

  // ── Submit ────────────────────────────────────────────────────────────────

  // FIX #18: wrapped in useCallback
  const handleSubmit = useCallback(async () => {
    // FIX #6: Use canSubmit for a single consistent gate
    if (!canSubmit) {
      showNotification('Please complete all steps and enter a valid quantity.', 'error');
      return;
    }
    if (!consumptionDate) {
      showNotification('Please enter a consumption date.', 'error');
      return;
    }

    setSubmitting(true);
    try {
      // FIX #2: use consumableSpareLogService.recordConsumption (not assetService)
      // FIX #3: pass all required model fields
      // FIX #14: pass uom from the selected item
      await consumableSpareLogService.recordConsumption(currentUser, {
        assetId:                  selectedItem.id,
        assetName:                selectedItem.assetName          || '',
        assetType:                selectedItem.assetType          || 'Consumable',
        consumedAgainstAssetId:   selectedTarget.id,
        consumedAgainstAssetName: selectedTarget.assetName        || '',
        quantity:                 parsedConsumeQty,
        uom:                      selectedItem.unitOfMeasurement  || 'Nos',
        consumptionDate,
        remarks:                  remarks.trim(),
      });

      showNotification(
        `Consumption recorded successfully. ${parsedConsumeQty} ` +
        `${selectedItem.unitOfMeasurement || 'unit(s)'} of ` +
        `"${selectedItem.assetName}" consumed. Inventory updated.`,
        'success',
      );

      // FIX #5: Use a single controller and ensure cleanup is handled
      // by loadAssets's own abort guard — no need to manually abort here
      // since loadAssets checks mountedRef internally.
      const ac = new AbortController();
      await loadAssets(ac.signal);

      // Refresh history in background — no await needed
      refreshHistory();

      handleReset();
    } catch (err) {
      showNotification(
        `Failed to record consumption: ${err.message}. You can retry or start over.`,
        'error',
      );
    } finally {
      if (mountedRef.current) setSubmitting(false);
    }
  }, [
    canSubmit, consumptionDate, currentUser,
    selectedItem, selectedTarget, parsedConsumeQty, remarks,
    loadAssets, refreshHistory, handleReset, showNotification,
  ]);

  // ── Guards ────────────────────────────────────────────────────────────────

  if (pageState === 'error') {
    return (
      <Box sx={{ p: 4 }}>
        <PermissionDenied message={permissionError} />
      </Box>
    );
  }

  if (pageState === 'loading') return <ConsumptionsSkeleton />;

  // ─────────────────────────────────────────────────────────────────────────
  // RENDER
  // ─────────────────────────────────────────────────────────────────────────
  return (
    <Container
      maxWidth="xl"
      sx={{ mt: 4, mb: 4, display: 'flex', flexDirection: 'column', gap: 3 }}
    >

      {/* Toast */}
      {notification && (
        <ToastNotification
          message={notification.message}
          type={notification.type}
          onClose={() => setNotification(null)}
        />
      )}

      {/* ── HEADER ─────────────────────────────────────────────────────────── */}
      <Box
        sx={{
          display:        'flex',
          justifyContent: 'space-between',
          alignItems:     'center',
          flexWrap:       'wrap',
          gap:            2,
        }}
      >
        <Box>
          <Typography
            variant="h4"
            component="h1"
            sx={{ fontWeight: 700, color: 'text.primary', letterSpacing: '-0.5px' }}
          >
            Consumptions
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Record consumption of Consumables and Spare Parts issued to assets
          </Typography>
        </Box>

        <Button
          variant="outlined"
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate(-1)}
          sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600 }}
        >
          Back
        </Button>
      </Box>

      {/* ── CONSUMPTION FORM ─────────────────────────────────────────────────── */}
      <Paper variant="outlined" sx={{ borderRadius: 3, overflow: 'hidden' }}>
        <Box
          sx={{
            px:           3,
            pt:           2.5,
            pb:           1.5,
            borderBottom: '1px solid',
            borderColor:  'divider',
          }}
        >
          <Typography variant="h6" fontWeight={700}>
            Record New Consumption
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Complete all 4 steps to record a consumption entry
          </Typography>
        </Box>

        <Box sx={{ p: 3 }}>
          {assetsLoading ? (
            <Box
              sx={{
                display: 'flex', alignItems: 'center', gap: 2,
                py: 4, justifyContent: 'center',
              }}
            >
              <CircularProgress size={24} />
              <Typography variant="body2" color="text.secondary">
                Loading inventory…
              </Typography>
            </Box>
          ) : (
            <Stepper activeStep={activeStep} orientation="vertical">

              {/* ── STEP 1: Select Item Type ─────────────────────────────── */}
              <Step>
                <StepLabel
                  optional={
                    selectedItemType && activeStep > 0 ? (
                      <Typography variant="caption" color="primary.main" fontWeight={600}>
                        Selected: {selectedItemType}
                      </Typography>
                    ) : null
                  }
                >
                  <Typography fontWeight={600}>Select Item Type</Typography>
                </StepLabel>

                <StepContent>
                  <Grid container spacing={2} sx={{ mt: 0.5 }}>
                    {ITEM_TYPES.map((type) => {
                      const Icon      = type.IconComponent;
                      const typeCount = allAssets.filter((a) => {
                        const qty = parseFloat(a.quantity);
                        return a.assetType === type.value && !isNaN(qty) && qty > 0;
                      }).length;

                      return (
                        <Grid item xs={12} sm={6} key={type.value}>
                          <Card
                            variant="outlined"
                            sx={{
                              borderRadius: 2,
                              border:       '2px solid',
                              borderColor:
                                selectedItemType === type.value
                                  ? `${type.color}.main`
                                  : 'divider',
                              transition: 'all 0.2s',
                              '&:hover': {
                                borderColor: `${type.color}.main`,
                                boxShadow:   1,
                              },
                            }}
                          >
                            <CardActionArea
                              onClick={() => handleSelectItemType(type.value)}
                              sx={{ p: 2 }}
                            >
                              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                                <Box sx={{ color: `${type.color}.main`, mt: 0.25 }}>
                                  <Icon />
                                </Box>
                                <Box sx={{ flex: 1 }}>
                                  <Box
                                    sx={{
                                      display:        'flex',
                                      alignItems:     'center',
                                      justifyContent: 'space-between',
                                    }}
                                  >
                                    <Typography variant="body1" fontWeight={700}>
                                      {type.label}
                                    </Typography>
                                    {selectedItemType === type.value ? (
                                      <CheckCircleIcon color={type.color} fontSize="small" />
                                    ) : (
                                      <RadioButtonUncheckedIcon color="disabled" fontSize="small" />
                                    )}
                                  </Box>
                                  <Typography variant="caption" color="text.secondary">
                                    {type.description}
                                  </Typography>
                                  <Box sx={{ mt: 0.75 }}>
                                    <Chip
                                      label={`${typeCount} available`}
                                      size="small"
                                      color={type.color}
                                      variant="outlined"
                                      sx={{ fontSize: '0.7rem', height: 20 }}
                                    />
                                  </Box>
                                </Box>
                              </Box>
                            </CardActionArea>
                          </Card>
                        </Grid>
                      );
                    })}
                  </Grid>
                </StepContent>
              </Step>

              {/* ── STEP 2: Select Item from Inventory ───────────────────── */}
              <Step>
                <StepLabel
                  optional={
                    selectedItem && activeStep > 1 ? (
                      <Typography variant="caption" color="primary.main" fontWeight={600}>
                        Selected: {selectedItem.assetName} (Qty: {selectedItem.quantity})
                      </Typography>
                    ) : null
                  }
                >
                  <Typography fontWeight={600}>Select Item from Inventory</Typography>
                </StepLabel>

                <StepContent>
                  <Box sx={{ mt: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <TextField
                      size="small"
                      fullWidth
                      placeholder={`Search ${selectedItemType} by name or ID…`}
                      value={itemSearch}
                      onChange={(e) => setItemSearch(e.target.value)}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <SearchIcon fontSize="small" color="action" />
                          </InputAdornment>
                        ),
                        endAdornment: itemSearch && (
                          <InputAdornment position="end">
                            {/* FIX #17: aria-label on clear buttons */}
                            <IconButton
                              size="small"
                              onClick={() => setItemSearch('')}
                              aria-label="Clear item search"
                            >
                              <ClearIcon fontSize="small" />
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                      sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                    />

                    {filteredItems.length === 0 ? (
                      <Alert severity="info" sx={{ borderRadius: 2 }}>
                        {availableItems.length === 0
                          ? `No ${selectedItemType} items with available quantity found.`
                          : 'No items match your search.'}
                      </Alert>
                    ) : (
                      <TableContainer
                        component={Paper}
                        variant="outlined"
                        sx={{ borderRadius: 2, maxHeight: 300, overflowY: 'auto' }}
                      >
                        <Table size="small" stickyHeader>
                          <TableHead>
                            <TableRow>
                              {['Item Name', 'Asset ID', 'Available Qty', 'Unit', 'Location', 'Select'].map((h) => (
                                <TableCell
                                  key={h}
                                  align={h === 'Available Qty' || h === 'Select' ? 'center' : 'left'}
                                  sx={{ fontWeight: 700, bgcolor: 'grey.50' }}
                                >
                                  {h}
                                </TableCell>
                              ))}
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {filteredItems.map((item) => (
                              <TableRow
                                key={item.id}
                                hover
                                selected={selectedItem?.id === item.id}
                                sx={{ cursor: 'pointer' }}
                                onClick={() => handleSelectItem(item)}
                              >
                                <TableCell sx={{ fontWeight: 600 }}>{item.assetName}</TableCell>
                                <TableCell
                                  sx={{
                                    fontFamily: 'monospace',
                                    fontSize:   '0.8rem',
                                    color:      'primary.main',
                                  }}
                                >
                                  {item.assetId}
                                </TableCell>
                                <TableCell align="center">
                                  <Chip
                                    label={item.quantity || 0}
                                    size="small"
                                    color={(parseFloat(item.quantity) || 0) <= 5 ? 'warning' : 'success'}
                                    sx={{ fontWeight: 700, fontSize: '0.75rem', minWidth: 40 }}
                                  />
                                </TableCell>
                                <TableCell sx={{ color: 'text.secondary', fontSize: '0.8rem' }}>
                                  {item.unitOfMeasurement || '—'}
                                </TableCell>
                                <TableCell sx={{ color: 'text.secondary', fontSize: '0.8rem' }}>
                                  {item.locationName || item.currentLocationName || '—'}
                                </TableCell>
                                <TableCell align="center">
                                  {selectedItem?.id === item.id ? (
                                    <CheckCircleIcon color="primary" fontSize="small" />
                                  ) : (
                                    <RadioButtonUncheckedIcon color="disabled" fontSize="small" />
                                  )}
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    )}

                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <Button
                        size="small" variant="outlined"
                        onClick={handleBack}
                        sx={{ textTransform: 'none' }}
                      >
                        Back
                      </Button>
                    </Box>
                  </Box>
                </StepContent>
              </Step>

              {/* ── STEP 3: Select Target Asset ──────────────────────────── */}
              <Step>
                <StepLabel
                  optional={
                    selectedTarget && activeStep > 2 ? (
                      <Typography variant="caption" color="primary.main" fontWeight={600}>
                        Selected: {selectedTarget.assetName}
                      </Typography>
                    ) : null
                  }
                >
                  <Typography fontWeight={600}>Select Target Asset</Typography>
                </StepLabel>

                <StepContent>
                  <Box sx={{ mt: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <Alert severity="info" sx={{ borderRadius: 2 }}>
                      <Typography variant="caption">
                        Select the <strong>Primary Asset</strong> or{' '}
                        <strong>Sub Asset</strong> this item is being consumed against.
                      </Typography>
                    </Alert>

                    <TextField
                      size="small"
                      fullWidth
                      placeholder="Search target asset by name or ID…"
                      value={targetSearch}
                      onChange={(e) => setTargetSearch(e.target.value)}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <SearchIcon fontSize="small" color="action" />
                          </InputAdornment>
                        ),
                        endAdornment: targetSearch && (
                          <InputAdornment position="end">
                            {/* FIX #17 */}
                            <IconButton
                              size="small"
                              onClick={() => setTargetSearch('')}
                              aria-label="Clear target search"
                            >
                              <ClearIcon fontSize="small" />
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                      sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                    />

                    {filteredTargets.length === 0 ? (
                      <Alert severity="warning" sx={{ borderRadius: 2 }}>
                        No Primary Assets or Sub Assets found.
                      </Alert>
                    ) : (
                      <TableContainer
                        component={Paper}
                        variant="outlined"
                        sx={{ borderRadius: 2, maxHeight: 300, overflowY: 'auto' }}
                      >
                        <Table size="small" stickyHeader>
                          <TableHead>
                            <TableRow>
                              {['Asset Name', 'Asset ID', 'Type', 'Location', 'Status', 'Select'].map((h) => (
                                <TableCell
                                  key={h}
                                  align={h === 'Select' ? 'center' : 'left'}
                                  sx={{ fontWeight: 700, bgcolor: 'grey.50' }}
                                >
                                  {h}
                                </TableCell>
                              ))}
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {filteredTargets.map((target) => (
                              <TableRow
                                key={target.id}
                                hover
                                selected={selectedTarget?.id === target.id}
                                sx={{ cursor: 'pointer' }}
                                onClick={() => handleSelectTarget(target)}
                              >
                                <TableCell sx={{ fontWeight: 600 }}>
                                  {target.assetName}
                                </TableCell>
                                <TableCell
                                  sx={{
                                    fontFamily: 'monospace',
                                    fontSize:   '0.8rem',
                                    color:      'primary.main',
                                  }}
                                >
                                  {target.assetId}
                                </TableCell>
                                <TableCell>
                                  <Chip
                                    label={target.assetType || 'Primary Asset'}
                                    size="small"
                                    variant="outlined"
                                    color={target.assetType === 'Sub Asset' ? 'default' : 'primary'}
                                    sx={{ fontSize: '0.68rem', height: 20 }}
                                  />
                                </TableCell>
                                <TableCell sx={{ color: 'text.secondary', fontSize: '0.8rem' }}>
                                  {target.locationName || target.currentLocationName || '—'}
                                </TableCell>
                                <TableCell>
                                  <Chip
                                    label={target.status || 'Available'}
                                    size="small"
                                    color={target.status === 'Assigned' ? 'success' : 'default'}
                                    sx={{ fontSize: '0.68rem', height: 20 }}
                                  />
                                </TableCell>
                                <TableCell align="center">
                                  {selectedTarget?.id === target.id ? (
                                    <CheckCircleIcon color="primary" fontSize="small" />
                                  ) : (
                                    <RadioButtonUncheckedIcon color="disabled" fontSize="small" />
                                  )}
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    )}

                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <Button
                        size="small" variant="outlined"
                        onClick={handleBack}
                        sx={{ textTransform: 'none' }}
                      >
                        Back
                      </Button>
                    </Box>
                  </Box>
                </StepContent>
              </Step>

              {/* ── STEP 4: Enter Consumption Details ────────────────────── */}
              <Step>
                <StepLabel>
                  <Typography fontWeight={600}>Enter Consumption Details</Typography>
                </StepLabel>

                <StepContent>
                  <Box sx={{ mt: 1, display: 'flex', flexDirection: 'column', gap: 2.5 }}>

                    {/* Summary banner */}
                    {selectedItem && selectedTarget && (
                      <Paper
                        variant="outlined"
                        sx={{
                          p:           2,
                          borderRadius: 2,
                          // FIX #16: use standard MUI tokens instead of primary.50/primary.200
                          bgcolor:     'primary.50',
                          borderColor: 'primary.200',
                        }}
                      >
                        <Typography
                          variant="caption"
                          color="primary.main"
                          fontWeight={700}
                          display="block"
                          sx={{ mb: 0.5 }}
                        >
                          CONSUMPTION SUMMARY
                        </Typography>
                        <Grid container spacing={1}>
                          <Grid item xs={12} sm={6}>
                            <Typography variant="body2" color="text.secondary">Item:</Typography>
                            <Typography variant="body2" fontWeight={700}>
                              {selectedItem.assetName}
                            </Typography>
                          </Grid>
                          <Grid item xs={12} sm={6}>
                            <Typography variant="body2" color="text.secondary">
                              Consuming Against:
                            </Typography>
                            <Typography variant="body2" fontWeight={700}>
                              {selectedTarget.assetName}
                            </Typography>
                          </Grid>
                          <Grid item xs={12} sm={6}>
                            <Typography variant="body2" color="text.secondary">Item Type:</Typography>
                            <Chip
                              label={selectedItemType}
                              size="small"
                              color="warning"
                              sx={{ fontSize: '0.7rem', height: 20, mt: 0.25 }}
                            />
                          </Grid>
                          <Grid item xs={12} sm={6}>
                            <Typography variant="body2" color="text.secondary">
                              Available Quantity:
                            </Typography>
                            <Typography
                              variant="body2"
                              fontWeight={700}
                              color={availableQty <= 5 ? 'warning.main' : 'success.main'}
                            >
                              {availableQty} {selectedItem.unitOfMeasurement || 'units'}
                            </Typography>
                          </Grid>
                        </Grid>
                      </Paper>
                    )}

                    {/* Low-stock warning */}
                    {availableQty > 0 && availableQty <= 5 && (
                      <Alert
                        severity="warning"
                        icon={<WarningAmberIcon />}
                        sx={{ borderRadius: 2 }}
                      >
                        <Typography variant="body2" fontWeight={600}>Low Stock Warning</Typography>
                        <Typography variant="caption">
                          Only {availableQty}{' '}
                          {selectedItem?.unitOfMeasurement || 'units'} remaining.
                          Consider restocking soon.
                        </Typography>
                      </Alert>
                    )}

                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={4}>
                        <TextField
                          fullWidth
                          size="small"
                          label="Consumed Quantity *"
                          type="number"
                          value={consumedQty}
                          onChange={(e) => setConsumedQty(e.target.value)}
                          onBlur={() => setQtyTouched(true)}
                          inputProps={{ min: 1, max: availableQty, step: 1 }}
                          error={qtyError}
                          // FIX #6: unified helper text
                          helperText={qtyHelperText}
                          sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                        />
                      </Grid>
                      <Grid item xs={12} sm={4}>
                        <TextField
                          fullWidth
                          size="small"
                          label="Consumption Date *"
                          type="date"
                          value={consumptionDate}
                          onChange={(e) => setConsumptionDate(e.target.value)}
                          InputLabelProps={{ shrink: true }}
                          sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                        />
                      </Grid>
                    </Grid>

                    <TextField
                      fullWidth
                      size="small"
                      label="Remarks"
                      multiline
                      rows={2}
                      placeholder="Optional notes about this consumption…"
                      value={remarks}
                      onChange={(e) => setRemarks(e.target.value)}
                      sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                    />

                    <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
                      <Button
                        variant="contained"
                        color="primary"
                        disableElevation
                        startIcon={
                          submitting
                            ? <CircularProgress size={16} color="inherit" />
                            : <SaveIcon />
                        }
                        onClick={handleSubmit}
                        // FIX #6: use canSubmit for consistent gating
                        disabled={submitting || !canSubmit}
                        sx={{
                          textTransform: 'none',
                          fontWeight:    600,
                          borderRadius:  2,
                          px:            3,
                          minWidth:      180,
                        }}
                      >
                        {submitting ? 'Recording…' : 'Record Consumption'}
                      </Button>

                      <Button
                        size="small" variant="outlined"
                        onClick={handleBack}
                        disabled={submitting}
                        sx={{ textTransform: 'none' }}
                      >
                        Back
                      </Button>

                      <Button
                        size="small" variant="text" color="inherit"
                        onClick={handleReset}
                        disabled={submitting}
                        sx={{ textTransform: 'none', color: 'text.secondary' }}
                      >
                        Start Over
                      </Button>
                    </Box>
                  </Box>
                </StepContent>
              </Step>

            </Stepper>
          )}
        </Box>
      </Paper>

      {/* ── CONSUMPTION HISTORY ──────────────────────────────────────────────── */}
      {/* Sentinel div — IntersectionObserver triggers lazy load */}
      <div ref={historySentinelRef} style={{ height: 1 }} aria-hidden="true" />

      <Paper variant="outlined" sx={{ borderRadius: 3, overflow: 'hidden' }}>
        <Box
          sx={{
            px:             3,
            py:             2,
            display:        'flex',
            justifyContent: 'space-between',
            alignItems:     'center',
            borderBottom:   '1px solid',
            borderColor:    'divider',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <HistoryIcon color="action" fontSize="small" />
            <Typography variant="h6" fontWeight={700}>
              Consumption History
            </Typography>
            {!historyLoading && (
              <Chip
                label={consumptionHistory.length}
                size="small"
                sx={{ fontWeight: 700, height: 20, fontSize: '0.72rem' }}
              />
            )}
            {historyLoading && (
              <CircularProgress size={14} thickness={5} sx={{ ml: 0.5 }} />
            )}
          </Box>
        </Box>

        {/* Loading skeleton for history */}
        {historyLoading ? (
          <TableContainer sx={{ overflowX: 'auto' }}>
            <Table size="medium">
              <TableHead>
                <TableRow>
                  {[
                    'S.No', 'Item Name', 'Item Type', 'Consumed Against',
                    'Quantity', 'Unit', 'Consumption Date', 'Recorded By', 'Remarks',
                  ].map((h) => (
                    <TableCell key={h} sx={{ fontWeight: 700 }}>{h}</TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                <HistoryRowSkeleton rows={5} />
              </TableBody>
            </Table>
          </TableContainer>
        ) : consumptionHistory.length === 0 ? (
          <Box sx={{ p: 6, textAlign: 'center' }}>
            <InventoryIcon sx={{ fontSize: 48, color: 'action.disabled', mb: 1.5 }} />
            <Typography variant="body1" color="text.secondary" fontWeight={600}>
              No consumption records yet
            </Typography>
            <Typography variant="body2" color="text.disabled" sx={{ mt: 0.5 }}>
              Use the form above to record your first consumption entry
            </Typography>
          </Box>
        ) : (
          <>
            <TableContainer sx={{ overflowX: 'auto' }}>
              <Table size="medium">
                <TableHead>
                  <TableRow>
                    {[
                      'S.No', 'Item Name', 'Item Type', 'Consumed Against',
                      'Quantity', 'Unit', 'Consumption Date', 'Recorded By', 'Remarks',
                    ].map((h) => (
                      <TableCell key={h} sx={{ fontWeight: 700 }}>{h}</TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {consumptionHistory
                    .slice(
                      histPage * histRowsPerPage,
                      histPage * histRowsPerPage + histRowsPerPage,
                    )
                    .map((record, idx) => (
                      <TableRow
                        key={record.id ?? `record-${record.consumptionDate}-${idx}`}
                        hover
                      >
                        <TableCell sx={{ color: 'text.secondary', fontWeight: 600 }}>
                          {histPage * histRowsPerPage + idx + 1}
                        </TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>
                          {record.assetName || '—'}
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={record.assetType || 'Consumable'}
                            size="small"
                            color={record.assetType === 'Spare Part' ? 'info' : 'warning'}
                            variant="outlined"
                            sx={{ fontSize: '0.7rem', height: 20 }}
                          />
                        </TableCell>
                        <TableCell sx={{ color: 'text.secondary', fontSize: '0.85rem' }}>
                          {/* FIX: prefer saved name, fall back to live lookup, then ID */}
                          {record.consumedAgainstAssetName ||
                            assetLookup[record.consumedAgainstAssetId]?.assetName ||
                            record.consumedAgainstAssetId ||
                            '—'}
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" fontWeight={700} color="primary.main">
                            {record.quantity}
                          </Typography>
                        </TableCell>
                        {/* FIX #11 / #14: Show uom column */}
                        <TableCell sx={{ color: 'text.secondary', fontSize: '0.85rem' }}>
                          {record.uom || '—'}
                        </TableCell>
                        <TableCell sx={{ color: 'text.secondary', fontSize: '0.85rem' }}>
                          {formatDate(record.consumptionDate || record.createdDate)}
                        </TableCell>
                        <TableCell
                          sx={{
                            fontFamily: 'monospace',
                            fontSize:   '0.78rem',
                            color:      'text.secondary',
                          }}
                        >
                          {record.createdByEmail || '—'}
                        </TableCell>
                        <TableCell sx={{ maxWidth: 200 }}>
                          <Tooltip
                            title={record.remarks || ''}
                            arrow
                            disableHoverListener={
                              !record.remarks || record.remarks.length < 30
                            }
                          >
                            <Typography
                              variant="body2"
                              color="text.secondary"
                              sx={{
                                overflow:     'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace:   'nowrap',
                                maxWidth:     180,
                              }}
                            >
                              {record.remarks || '—'}
                            </Typography>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))}
                </TableBody>
              </Table>
            </TableContainer>

            <Box
              sx={{
                display:        'flex',
                justifyContent: 'space-between',
                alignItems:     'center',
                py:             1.5,
                px:             2,
                borderTop:      '1px solid',
                borderColor:    'divider',
              }}
            >
              <Typography variant="caption" color="text.secondary" fontWeight={500}>
                Showing {histPage * histRowsPerPage + 1} to{' '}
                {Math.min(
                  (histPage + 1) * histRowsPerPage,
                  consumptionHistory.length,
                )}{' '}
                of {consumptionHistory.length} records
              </Typography>
              <TablePagination
                rowsPerPageOptions={[5, 10, 25]}
                component="div"
                count={consumptionHistory.length}
                rowsPerPage={histRowsPerPage}
                page={histPage}
                onPageChange={(_, newPage) => setHistPage(newPage)}
                onRowsPerPageChange={(e) => {
                  setHistRowsPerPage(parseInt(e.target.value, 10));
                  setHistPage(0);
                }}
                sx={{ border: 'none', overflow: 'hidden' }}
              />
            </Box>
          </>
        )}
      </Paper>

    </Container>
  );
};

export default Consumptions;